import { Intersection, LineSegments, Object3D, Vector3 } from 'three';
import _ from 'underscore';

import { isDefined } from '../../../../../utils/variables';
import { isStyledLines } from '../../../styled-lines/styled-lines';
import { Stripe } from '../../../styled-lines/styled-segment';
import { AttributeData, AttributeId } from '../../skymap-extras';
import { DesignNode, Revert } from '../design-node';
import { getConicLength, getSegmentConic, SegmentCurve } from './conic';
import { FaceId } from './face-node';
import { PointId } from './point-node';

export type LineSegmentId = number & { __type: 'LineId' };

export type LineSegmentNode = {
  id: LineSegmentId;
  values: [PointId, PointId];
  attributes: Map<AttributeId, AttributeData>;
  /**
   * Curves the line segment, relative to it so that it keeps its shape when the ends move,
   * see conic.ts. The key is always set, so that reverting a change restores a straight line segment.
   */
  curve: SegmentCurve | undefined;
  /**
   * Width of the line, in meters. 0 is a thin line.
   */
  width: number;
  /**
   * Draws the line in stripes. The pattern is an index into the stripe patterns of the design node.
   * The key is always set, so that reverting a change restores a line segment without stripes.
   */
  stripe: Stripe | undefined;
  length: number;
  planarLength: number;
};

export function getLineSegmentFaces(
  designNode: DesignNode,
  lineSegmentId: LineSegmentId,
): FaceId[] {
  const node = designNode.getLineSegmentById(lineSegmentId);
  const start = designNode.getPointById(node?.values[0]);
  const end = designNode.getPointById(node?.values[1]);
  return _.intersection(start.faces, end.faces);
}

/**
 * Whether the object draws line segments of a design: straight thin lines, or styled lines.
 */
export const isLineSegmentObject = (object?: Object3D) =>
  object instanceof LineSegments || isStyledLines(object);

/**
 * Whether the line segment is drawn by StyledLines: curved, with a width or with stripes.
 */
export const isLineSegmentStyled = (node: Pick<LineSegmentNode, 'curve' | 'width' | 'stripe'>) =>
  isDefined(node.curve) || node.width > 0 || isDefined(node.stripe);

/**
 * Gets the length of a line segment, along its curve if it is curved, and its length in the
 * horizontal plane.
 */
export function getLineSegmentLengths(
  start: Vector3,
  end: Vector3,
  curve: SegmentCurve | undefined,
): { length: number; planarLength: number } {
  if (!curve) {
    return {
      length: start.distanceTo(end),
      planarLength: Math.hypot(end.x - start.x, end.y - start.y),
    };
  }

  const conic = getSegmentConic(start, end, curve);
  return { length: getConicLength(conic), planarLength: getConicLength(conic, 0, 1, true) };
}

/**
 * An intersection with a line segment where it crosses another one, see the intersection snap
 * style. A point placed there breaks both line segments.
 */
export type CrossingIntersection = Intersection & { crossedLineSegmentId?: LineSegmentId };

/**
 * Gets the line segment that the line segment of an intersection crosses there, if it still exists.
 */
export function findCrossedLineSegmentIdFromIntersection(
  node: DesignNode,
  intersection?: Intersection,
): LineSegmentId | undefined {
  const id = (intersection as CrossingIntersection | undefined)?.crossedLineSegmentId;
  return isDefined(id) && node.lineSegments.includes(id) ? id : undefined;
}

export function findLineSegmentIdFromIntersection(
  node: DesignNode,
  intersection?: Intersection,
): LineSegmentId | undefined {
  return isDefined(intersection) &&
    isLineSegmentObject(intersection.object) &&
    isDefined(intersection.index)
    ? node.getLineSegmentByIndex(intersection.index).id
    : undefined;
}

/**
 * A line is considered an edge line if there is exaclty 1 face connected to it.
 */
export function lineSegmentIsEdgeLine(node: DesignNode, id: LineSegmentId) {
  return getLineSegmentFaces(node, id).length === 1;
}

/**
 * A line is considered a break line if there is more than 1 face adjacent to it.
 */
export function lineSegmentIsBreakLine(node: DesignNode, id: LineSegmentId) {
  return getLineSegmentFaces(node, id).length > 1;
}

/**
 * A line segment is counted as part of line if there are no faces connected to it.
 */
export function lineSegmentIsPartOfLine(node: DesignNode, id: LineSegmentId) {
  return getLineSegmentFaces(node, id).length === 0;
}

export function recalculateLineSegmentMetaData(node: DesignNode, ids: LineSegmentId[]) {
  for (const id of ids) {
    const lineSegment = node.getLineSegmentById(id);
    const vertices = node.getVerticesByLineSegmentId(id, 'local');
    const { length, planarLength } = getLineSegmentLengths(
      vertices[0],
      vertices[1],
      lineSegment.curve,
    );
    lineSegment.length = length;
    lineSegment.planarLength = planarLength;
  }
}

export function addLineSegmentsToDesignNode(
  node: DesignNode,
  ids: PointId[],
  attributeData?: Map<AttributeId, AttributeData>,
): Revert<'remove-linesegment'>[] {
  if (ids.length % 2 !== 0) {
    throw new Error('Point list for creating line segments needs to be divisible by 2.');
  }

  const changes: Revert<'remove-linesegment'>[] = [];
  for (let i = 0; i < ids.length; i += 2) {
    changes.push(node.addLineSegment(ids[i], ids[i + 1], attributeData));
  }

  return changes;
}
